<template>
  <AuthForm title="sign_in_instruction" subtitle="welcome_back_subtitle">
    <HealthInputText
      ref="loginInputRef"
      v-model="securityModel.email"
      required
      autofocus
      class="col-span-12"
      label="Login"
      :placeholder="$t('enter_login')"
      icon="pi pi-user"
      :error="!!errors?.email"
      :error-message="errors?.email"
    />

    <HealthPassword
      v-model="securityModel.password"
      required
      class="col-span-12"
      :label="t('password')"
      :placeholder="$t('enter_password')"
      icon="pi pi-lock"
      :error="!!errors?.password"
    />

    <RouterLink
      to="/"
      class="col-span-12 text-end text-xs text-primary-500 dark:text-white/70 hover:text-white ml-auto -mt-2 transition-colors"
    >
      {{ t("forgot_password_question") }}
    </RouterLink>

    <HealthButton
      :loading="loading || loadingInstitutions"
      size="py-3"
      class="col-span-12"
      :label="t('sign_in')"
      icon="prime:sign-in"
      label-class="block"
      type="submit"
      @action="login"
    />

    <div class="col-span-12 flex items-center gap-4 my-4">
      <div class="flex-grow h-px bg-gray-300 dark:bg-gray-600"></div>
      <span
        class="text-[10px] text-gray-400 dark:text-gray-400 uppercase tracking-widest font-bold whitespace-nowrap"
      >
        {{ t("need_help") }}
      </span>
      <div class="flex-grow h-px bg-gray-300 dark:bg-gray-600"></div>
    </div>

    <div class="col-span-12 flex justify-center">
      <a
        href="https://www.zafaz.com.br"
        target="_blank"
        class="text-primary-500 dark:text-white font-bold text-sm flex items-center gap-2 hover:opacity-80 transition-opacity"
      >
        <Icon icon="solar:headphones-round-bold" class="text-xl" />
        {{ t("contact_support") }}
      </a>
    </div>
  </AuthForm>
</template>

<script setup lang="ts">
  import { useAuth } from "@/shared/stores/auth";
  import { useI18n } from "vue-i18n";
  import { useRouter } from "vue-router";
  import { loginController } from "@/app/security/controllers/loginController";
  import { useForm } from "@/shared/composables/form/useForm";
  import { getAllInstitutionsController } from "@/app/institution/controllers/getAllInstitutionsController";
  import { useInstitution } from "@/shared/stores/institution";
  import HealthInputText from "@/shared/components/form/input_text/HealthInputText.vue";
  import HealthPassword from "@/shared/components/form/password/HealthPassword.vue";
  import AuthForm from "@/app/security/AuthForm.vue";
  import HealthButton from "@/shared/components/utils/button/HealthButton.vue";
  import { SecurityModel } from "@/app/security/models/SecurityModel";
  import { ref, onMounted } from "vue";
  import { Icon } from "@iconify/vue";

  const loginInputRef = ref(null);

  const { t } = useI18n();
  const router = useRouter();
  const institutionStore = useInstitution();

  const { setUser, setAccessToken, setRefreshToken } = useAuth();

  const {
    request: getAllInstitutions,
    data: institutions,
    loading: loadingInstitutions,
  } = getAllInstitutionsController();

  const {
    modelClass: securityModel,
    clearModel,
    errors,
    isValid,
    validateModel,
  } = useForm(SecurityModel);

  const { request: loginRequest, loading, data: auth } = loginController();

  async function login() {
    await validateModel();

    if (isValid()) {
      await loginRequest(securityModel.value).then(async () => {
        setUser(auth.value.user);
        setAccessToken(auth.value.accessToken);
        setRefreshToken(auth.value.refreshToken);

        await getAllInstitutions().then(() => {
          institutionStore.setInstitutions(institutions.value);
          institutionStore.setInstitutionId(Number(institutions.value[0]?.id));
        });

        await router.push("/exams");

        clearModel();
      });
    }
  }

  onMounted(() => {
    loginInputRef.value?.focus();
  });
</script>
